import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import IconStarFill from "../../../components/icons/icon-star-fill";
import IconStarLine from "../../../components/icons/icon-star-line";
import IconHeart from "../../../components/icons/icon-heart";
import IconChatBubbleSquare from "../../../components/icons/icon-chat-bubble-square";
import IconAnonymous from "../../../components/icons/icon-anonymous";
import { useReviews } from "../hooks/use-reviews";
import type { PlanLimitErrorInfo } from "../stores/review-store";
import { useAuth } from "../../auth/hooks/use-auth";
import { useTags } from "../../tag/hooks/use-tags";
import { TAG_COLORS } from "../../tag/colors";
import { videoUploadApi } from "../services/video-upload-api";
import { reviewUploadsApi } from "../services/review-uploads-api";
import type { ReviewResponse } from "../types";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Button from "@/components/ui/button";
import Select from "@/components/ui/select";
import Container from "@/components/ui/container";
import TagSelector from "@/components/ui/tag-selector";
import VideoUploadField from "@/components/ui/video-upload-field";
import AudioPlayer from "@/components/ui/audio-player";
import AudioUploadField from "@/components/ui/audio-upload-field";
import ImageUploadField from "@/components/ui/image-upload-field";
import { useDirtyState } from "@/modules/form/components/form-builder/hooks/use-dirty-state";
import { useBrands, usePlan } from "@/modules/brand";
import { hasReachedReviewLimit } from "@/modules/brand/utils/plan-restrictions";
import { useOpenUpgradeModal } from "@/hooks/use-upgrade-modal";

const reviewSchema = z.object({
	// Required fields
	contentText: z.string(),
	date: z.string().min(1, "Date is required"),
	customerName: z.string().min(1, "Customer name is required"),
	consent: z.enum(["public", "private", "anonymous"], {
		message: "Please select a consent option",
	}),
	// Rating (nullable for "without rating" mode)
	rating: z.number().min(0).max(5).nullable(),
	// Optional fields
	customerHeadline: z.string().optional(),
	customerEmail: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
	customerPhone: z
		.string()
		.regex(/^$|^[+]?[\d\s\-().]{7,}$/, "Please enter a valid phone number")
		.optional(),
	customerJobTitle: z.string().optional(),
	customerCompanyName: z.string().optional(),
	customerCompanyWebsiteUrl: z.string().optional(),
});

export type ReviewFormData = z.infer<typeof reviewSchema>;

interface ManualReviewFormProps {
	onSuccess: (review: ReviewResponse) => void;
	onCancel: () => void;
	editMode?: boolean;
	initialData?: ReviewResponse | null;
}

function StarRatingInput({ value, onChange, faded }: { value: number; onChange: (rating: number) => void; faded?: boolean }) {
	const [hoverValue, setHoverValue] = useState(0);

	const displayValue = hoverValue > 0 ? hoverValue : value;

	return (
		<div className={`flex items-center gap-2 transition-opacity ${faded ? "opacity-50" : ""}`} onMouseLeave={() => setHoverValue(0)}>
			{[1, 2, 3, 4, 5].map((star) => (
				<button
					key={star}
					type="button"
					onClick={() => onChange(star)}
					onMouseEnter={() => setHoverValue(star)}
					className="p-0.5 transition-transform cursor-pointer hover:scale-110"
					aria-label={`Rate ${star} stars`}
				>
					{star <= displayValue ? (
						<IconStarFill className={`w-12 h-12 ${hoverValue > 0 && star <= hoverValue ? "text-brand/70" : "text-brand"}`} />
					) : (
						<IconStarLine className={`w-12 h-12 ${faded ? "text-gray-300" : "text-gray-300 hover:text-brand/50"}`} />
					)}
				</button>
			))}
		</div>
	);
}

// Helper to convert base64 data URL to Blob
function dataUrlToBlob(dataUrl: string): Blob {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new Blob([u8arr], { type: mime });
}

interface UploadStatus {
	type: "video" | "audio" | "avatar" | "logo";
	status: "pending" | "uploading" | "completed" | "error";
	progress: number;
	fileName: string;
}

// Upload progress modal component
function UploadProgressModal({ uploads }: { uploads: UploadStatus[] }) {
	const getIcon = (type: UploadStatus["type"]) => {
		switch (type) {
			case "video":
				return (
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
					/>
				);
			case "audio":
				return (
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
					/>
				);
			case "avatar":
				return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />;
			case "logo":
				return (
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
					/>
				);
		}
	};

	const getLabel = (type: UploadStatus["type"]) => {
		switch (type) {
			case "video":
				return "Video";
			case "audio":
				return "Audio";
			case "avatar":
				return "Avatar";
			case "logo":
				return "Logo";
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-xl">
				<div className="flex items-center gap-4 mb-6">
					<div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
						<svg className="w-6 h-6 text-brand animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
						</svg>
					</div>
					<div>
						<h3 className="text-lg font-semibold text-primary">Uploading Files</h3>
						<p className="text-sm text-tertiary">Please wait while your files are being uploaded...</p>
					</div>
				</div>

				<div className="space-y-4">
					{uploads.map((upload) => (
						<div key={upload.type} className="flex items-center gap-3">
							<div
								className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
									upload.status === "completed" ? "bg-green-100" : upload.status === "error" ? "bg-red-100" : "bg-gray-100"
								}`}
							>
								{upload.status === "completed" ? (
									<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
								) : upload.status === "error" ? (
									<svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								) : (
									<svg className={`w-4 h-4 ${upload.status === "uploading" ? "text-brand" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
										{getIcon(upload.type)}
									</svg>
								)}
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center justify-between mb-1">
									<span className="text-sm font-medium text-primary">{getLabel(upload.type)}</span>
									<span className="text-xs text-tertiary">
										{upload.status === "completed" ? "Done" : upload.status === "error" ? "Failed" : upload.status === "uploading" ? `${upload.progress}%` : "Waiting"}
									</span>
								</div>
								<div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
									<div
										className={`h-full transition-all duration-300 ease-out rounded-full ${
											upload.status === "completed" ? "bg-green-500" : upload.status === "error" ? "bg-red-500" : "bg-brand"
										}`}
										style={{ width: `${upload.status === "completed" ? 100 : upload.progress}%` }}
									/>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

type ReviewFormat = "text" | "video" | "audio";

// Helper to format date for HTML date input (YYYY-MM-DD)
function formatDateForInput(dateStr: string | undefined): string {
	if (!dateStr) return new Date().toISOString().split("T")[0];
	// Handle ISO format (e.g., "2026-01-14T00:00:00.000Z")
	if (dateStr.includes("T")) {
		return dateStr.split("T")[0];
	}
	// Already in YYYY-MM-DD format
	return dateStr;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

export default function ManualReviewForm({ onSuccess, onCancel, editMode = false, initialData = null }: ManualReviewFormProps) {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	// Track sidebar collapsed state for sticky footer positioning
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
		const saved = localStorage.getItem("sidebar-collapsed");
		return saved ? JSON.parse(saved) : false;
	});

	// Listen for sidebar collapse changes
	useEffect(() => {
		const handleSidebarChange = (e: CustomEvent<{ isCollapsed: boolean }>) => {
			setIsSidebarCollapsed(e.detail.isCollapsed);
		};

		window.addEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);

		return () => {
			window.removeEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);
		};
	}, []);
	// Initialize format from existing data (locked in edit mode)
	const [reviewFormat, setReviewFormat] = useState<ReviewFormat>(editMode && initialData ? (initialData.content_type as ReviewFormat) : "text");
	// Rating mode: with-rating shows stars, without-rating hides them
	const [ratingMode, setRatingMode] = useState<"with-rating" | "without-rating">(() => {
		if (editMode && initialData) {
			// If review has a rating > 0, use "with-rating", otherwise "without-rating"
			return initialData.rating && initialData.rating > 0 ? "with-rating" : "without-rating";
		}
		return "with-rating"; // Default for create mode
	});
	// Manual type selection for "without rating" mode
	const [manualType, setManualType] = useState<"testimonial" | "feedback" | null>(() => {
		if (editMode && initialData && (!initialData.rating || initialData.rating === 0)) {
			// If no rating, use the existing type
			return (initialData.type as "testimonial" | "feedback") || null;
		}
		return null; // Default for create mode
	});
	const [source, setSource] = useState<string>(editMode && initialData?.source ? initialData.source : "manual");
	const [sourcePostUrl, setSourcePostUrl] = useState<string>(editMode && initialData?.source_post_url ? initialData.source_post_url : "");
	const [selectedTagIds, setSelectedTagIds] = useState<string[]>(editMode && initialData?.tags ? initialData.tags.map((t) => t.id) : []);
	// File states (for post-save upload) - in edit mode, video/audio cannot be changed
	const [videoFile, setVideoFile] = useState<File | null>(null);
	const [audioFile, setAudioFile] = useState<File | null>(null);
	const [avatarImage, setAvatarImage] = useState<string | null>(editMode && initialData?.customer_avatar_url ? initialData.customer_avatar_url : null);
	const [companyLogoImage, setCompanyLogoImage] = useState<string | null>(editMode && initialData?.customer_company_logo_url ? initialData.customer_company_logo_url : null);
	// Upload progress state
	const [isUploading, setIsUploading] = useState(false);
	const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
	const [tagsNextCursor, setTagsNextCursor] = useState<string | null>(null);
	const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false);
	const { currentWorkspaceId } = useAuth();
	const { createReview, updateReview } = useReviews();
	const { tags, indexTags, searchTags, createTag, getTagsByIds, mergeSelectedTags } = useTags();
	const { selectedBrand, viewBrand } = useBrands();
	const { planDetails, getPlan } = usePlan();
	const openUpgradeModal = useOpenUpgradeModal();

	// Load brand data to get user role and plan data for limits
	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrand(currentWorkspaceId);
			getPlan(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrand, getPlan]);

	// Track the baseline data for comparison (updated after successful saves)
	const [baselineData, setBaselineData] = useState<ReviewResponse | null>(initialData);

	// Get initial selected tag IDs from initialData (stable reference using useMemo)
	const initialSelectedTagIds = useMemo(
		() => (editMode && initialData?.tags ? initialData.tags.map((t) => t.id) : []),
		// Only depend on initialData?.id to ensure stability - tags won't change for same review
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[initialData?.id],
	);

	// Load tags on mount
	useEffect(() => {
		if (!currentWorkspaceId) return;

		(async () => {
			const response = await indexTags(currentWorkspaceId);
			if (response) {
				setTagsNextCursor(response.meta.next_cursor);
			}
		})();
	}, [currentWorkspaceId, indexTags]);

	// Fetch and merge selected tags separately (only when we have initial selected tags)
	useEffect(() => {
		if (!currentWorkspaceId || initialSelectedTagIds.length === 0) return;

		(async () => {
			const selectedTags = await getTagsByIds(currentWorkspaceId, initialSelectedTagIds);
			if (selectedTags) {
				mergeSelectedTags(selectedTags);
			}
		})();
	}, [currentWorkspaceId, initialSelectedTagIds, getTagsByIds, mergeSelectedTags]);

	const handleLoadMoreTags = useCallback(async () => {
		if (!currentWorkspaceId || !tagsNextCursor || isLoadingMoreTags) return;

		setIsLoadingMoreTags(true);
		const response = await indexTags(currentWorkspaceId, tagsNextCursor);
		if (response) {
			setTagsNextCursor(response.meta.next_cursor);
		}
		setIsLoadingMoreTags(false);
	}, [currentWorkspaceId, tagsNextCursor, isLoadingMoreTags, indexTags]);

	// Handle searching tags
	const handleSearchTags = useCallback(
		async (query: string, cursor?: string | null) => {
			if (!currentWorkspaceId) return null;
			const response = await searchTags(currentWorkspaceId, query, cursor);
			if (response) {
				return {
					tags: response.data,
					hasMore: !!response.meta.next_cursor,
					nextCursor: response.meta.next_cursor,
				};
			}
			return null;
		},
		[currentWorkspaceId, searchTags],
	);

	// Handle creating a new tag
	const handleCreateTag = useCallback(
		(label: string, onCreated: (tagId: string) => void) => {
			if (!currentWorkspaceId) return;
			const randomColor = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)].value;
			createTag(currentWorkspaceId, { label, color: randomColor }).then((response) => {
				if (response.data?.id) {
					onCreated(response.data.id);
				}
			});
		},
		[currentWorkspaceId, createTag],
	);

	// Handle tag selection - merge into store so it remains visible
	const handleTagSelect = useCallback(
		(tag: { id: string; label: string; color: string }) => {
			mergeSelectedTags([tag]);
		},
		[mergeSelectedTags],
	);

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		trigger,
		reset,
		formState: { errors, isValid, isDirty },
	} = useForm<ReviewFormData>({
		resolver: zodResolver(reviewSchema),
		mode: "onChange",
		defaultValues:
			editMode && initialData
				? {
						// Pre-populate from existing data
						contentText: initialData.content_text || "",
						date: formatDateForInput(initialData.date),
						customerName: initialData.customer_name || "",
						consent: (initialData.consent as "public" | "private" | "anonymous") || "public",
						// Rating
						rating: initialData.rating || 0,
						// Optional fields
						customerHeadline: initialData.customer_headline || "",
						customerEmail: initialData.customer_email || "",
						customerPhone: initialData.customer_phone || "",
						customerJobTitle: initialData.customer_job_title || "",
						customerCompanyName: initialData.customer_company_name || "",
						customerCompanyWebsiteUrl: initialData.customer_company_website_url || "",
					}
				: {
						// Default values for create mode
						contentText: "",
						date: new Date().toISOString().split("T")[0],
						customerName: "",
						consent: "public" as const,
						// Rating
						rating: 0,
						// Optional fields
						customerHeadline: "",
						customerEmail: "",
						customerPhone: "",
						customerJobTitle: "",
						customerCompanyName: "",
						customerCompanyWebsiteUrl: "",
					},
	});

	const ratingValue = watch("rating");
	const consent = watch("consent");
	const contentText = watch("contentText");

	// Custom validity check that includes state-based fields
	const isFormComplete = (() => {
		// Check schema validity first
		if (!isValid) return false;

		// Check content/file requirements based on format
		if (reviewFormat === "text") {
			if (!contentText || contentText.trim() === "") return false;
		} else if (reviewFormat === "video") {
			// In create mode, video file is required
			if (!editMode && !videoFile) return false;
		} else if (reviewFormat === "audio") {
			// In create mode, audio file is required
			if (!editMode && !audioFile) return false;
		}

		// Check rating requirements based on rating mode
		if (ratingMode === "with-rating") {
			// Rating must be selected (1-5 stars)
			if (!ratingValue || ratingValue < 1) return false;
		} else {
			// Without rating mode: manual type selection required
			if (!manualType) return false;
		}

		return true;
	})();

	// Add hasChanges logic - compare against baselineData (which updates after saves)
	const baselineRatingMode = editMode && baselineData && baselineData.rating && baselineData.rating > 0 ? "with-rating" : "without-rating";
	const baselineManualType = editMode && baselineData && (!baselineData.rating || baselineData.rating === 0) ? baselineData.type : null;
	const hasNonFormChanges =
		editMode && baselineData
			? source !== (baselineData.source || "manual") ||
				sourcePostUrl !== (baselineData.source_post_url || "") ||
				JSON.stringify(selectedTagIds.slice().sort()) !== JSON.stringify((baselineData.tags || []).map((t) => t.id).sort()) ||
				(avatarImage !== baselineData.customer_avatar_url && !!(avatarImage && avatarImage.startsWith("data:"))) ||
				(companyLogoImage !== baselineData.customer_company_logo_url && !!(companyLogoImage && companyLogoImage.startsWith("data:"))) ||
				ratingMode !== baselineRatingMode ||
				(ratingMode === "without-rating" && manualType !== baselineManualType)
			: false;

	const hasChanges = !editMode || isDirty || hasNonFormChanges;

	// Set up dirty state tracking and navigation guards
	useDirtyState({ hasUnsavedChanges: hasChanges });

	// Add handleDiscard function - resets to baseline (last saved) values
	const handleDiscard = () => {
		if (editMode && baselineData) {
			reset({
				contentText: baselineData.content_text || "",
				date: formatDateForInput(baselineData.date),
				customerName: baselineData.customer_name || "",
				consent: (baselineData.consent as "public" | "private" | "anonymous") || "public",
				rating: baselineData.rating || 0,
				customerHeadline: baselineData.customer_headline || "",
				customerEmail: baselineData.customer_email || "",
				customerPhone: baselineData.customer_phone || "",
				customerJobTitle: baselineData.customer_job_title || "",
				customerCompanyName: baselineData.customer_company_name || "",
				customerCompanyWebsiteUrl: baselineData.customer_company_website_url || "",
			});
			setSource(baselineData.source || "manual");
			setSourcePostUrl(baselineData.source_post_url || "");
			setSelectedTagIds(baselineData.tags ? baselineData.tags.map((t) => t.id) : []);
			setAvatarImage(baselineData.customer_avatar_url || null);
			setCompanyLogoImage(baselineData.customer_company_logo_url || null);
			setRatingMode(baselineData.rating && baselineData.rating > 0 ? "with-rating" : "without-rating");
			setManualType(!baselineData.rating || baselineData.rating === 0 ? (baselineData.type as "testimonial" | "feedback") || null : null);
			setError(null);
		} else {
			onCancel();
		}
	};

	const onSubmit = async (data: ReviewFormData) => {
		if (!currentWorkspaceId) {
			setError("Workspace not found. Please refresh the page.");
			return;
		}

		// Validate content for text format
		if (reviewFormat === "text" && (!data.contentText || data.contentText.trim() === "")) {
			setError("Please enter the review content.");
			return;
		}

		// Validate video file for video format (only in create mode)
		if (!editMode && reviewFormat === "video" && !videoFile) {
			setError("Please select a video file before saving.");
			return;
		}

		// Validate audio file for audio format (only in create mode)
		if (!editMode && reviewFormat === "audio" && !audioFile) {
			setError("Please select an audio file before saving.");
			return;
		}

		// Validate classification selection for without-rating mode
		if (ratingMode === "without-rating" && !manualType) {
			setError("Please select a classification (Testimonial or Feedback).");
			return;
		}

		// Clear any existing timeout
		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setIsLoading(true);
		setError(null);
		setSaveStatus("saving");

		try {
			// Determine the type based on rating mode
			// With rating: server determines type based on rating value
			// Without rating: use manual selection, default to feedback if not selected
			const reviewType = ratingMode === "without-rating" ? manualType || "feedback" : undefined;

			// Build the API request (convert camelCase to snake_case for backend)
			const reviewRequest = {
				// Required fields (type is only sent for without-rating mode)
				...(reviewType && { type: reviewType as "testimonial" | "feedback" }),
				content_type: reviewFormat as "text" | "video" | "audio",
				content_text: data.contentText,
				date: data.date,
				source: source,
				// Consent: in edit mode, only include if manually added
				consent: data.consent as "public" | "private" | "anonymous",
				customer_name: data.customerName,
				// Optional fields - rating is null when in "without-rating" mode
				rating: ratingMode === "without-rating" ? null : data.rating,
				customer_headline: data.customerHeadline || undefined,
				customer_email: data.customerEmail || undefined,
				customer_phone: data.customerPhone || undefined,
				customer_job_title: data.customerJobTitle || undefined,
				customer_company_name: data.customerCompanyName || undefined,
				customer_company_website_url: data.customerCompanyWebsiteUrl || undefined,
				source_post_url: sourcePostUrl || undefined,
				// Always send tag_ids to allow clearing tags (empty array clears, undefined = no change)
				tag_ids: selectedTagIds,
			};

			// Step 1: Create or Update the review
			let result: ReviewResponse | null;
			if (editMode && initialData) {
				result = await updateReview(currentWorkspaceId, initialData.id, reviewRequest);
			} else {
				result = await createReview(currentWorkspaceId, reviewRequest);
			}

			if (!result) {
				setError(`Failed to ${editMode ? "update" : "create"} review. Please try again.`);
				setIsLoading(false);
				return;
			}

			// Step 2: Upload files if present
			// In edit mode: only upload new images (base64 data URLs), no video/audio
			// In create mode: upload all files
			const isNewImage = (img: string | null) => img && img.startsWith("data:");
			const hasFilesToUpload = editMode ? isNewImage(avatarImage) || isNewImage(companyLogoImage) : videoFile || audioFile || avatarImage || companyLogoImage;

			if (hasFilesToUpload) {
				setIsLoading(false);
				setIsUploading(true);

				// Build initial upload statuses
				const initialStatuses: UploadStatus[] = [];
				// Video/audio only in create mode
				if (!editMode && videoFile) initialStatuses.push({ type: "video", status: "pending", progress: 0, fileName: videoFile.name });
				if (!editMode && audioFile) initialStatuses.push({ type: "audio", status: "pending", progress: 0, fileName: audioFile.name });
				// Images: only new base64 images
				if (isNewImage(avatarImage)) initialStatuses.push({ type: "avatar", status: "pending", progress: 0, fileName: "avatar.png" });
				if (isNewImage(companyLogoImage)) initialStatuses.push({ type: "logo", status: "pending", progress: 0, fileName: "logo.png" });

				setUploadStatuses(initialStatuses);

				const updateStatus = (type: UploadStatus["type"], updates: Partial<UploadStatus>) => {
					setUploadStatuses((prev) => prev.map((s) => (s.type === type ? { ...s, ...updates } : s)));
				};

				// Track upload failures
				let hasUploadFailure = false;
				const failedUploads: string[] = [];

				// Upload files in parallel
				const uploadPromises: Promise<void>[] = [];

				// Video upload (create mode only)
				if (!editMode && videoFile) {
					uploadPromises.push(
						(async () => {
							updateStatus("video", { status: "uploading" });
							try {
								const uploadUrlResult = await videoUploadApi.getUploadUrl(currentWorkspaceId, result.id);
								if (!uploadUrlResult.success || !uploadUrlResult.data) {
									throw new Error(uploadUrlResult.message || "Failed to get upload URL");
								}
								await videoUploadApi.uploadToMux(uploadUrlResult.data.upload_url, videoFile, (progress) => updateStatus("video", { progress }));
								updateStatus("video", { status: "completed", progress: 100 });
							} catch (err) {
								console.error("Video upload failed:", err);
								updateStatus("video", { status: "error" });
								hasUploadFailure = true;
								failedUploads.push("Video");
							}
						})(),
					);
				}

				// Audio upload (create mode only)
				if (!editMode && audioFile) {
					uploadPromises.push(
						(async () => {
							updateStatus("audio", { status: "uploading" });
							try {
								await reviewUploadsApi.uploadAudio(currentWorkspaceId, result.id, audioFile, (progress) => updateStatus("audio", { progress }));
								updateStatus("audio", { status: "completed", progress: 100 });
							} catch (err) {
								console.error("Audio upload failed:", err);
								updateStatus("audio", { status: "error" });
								hasUploadFailure = true;
								failedUploads.push("Audio");
							}
						})(),
					);
				}

				// Avatar upload (only new base64 images)
				if (isNewImage(avatarImage) && avatarImage) {
					uploadPromises.push(
						(async () => {
							updateStatus("avatar", { status: "uploading", progress: 50 });
							try {
								const blob = dataUrlToBlob(avatarImage);
								const file = new File([blob], "avatar.png", { type: blob.type });
								await reviewUploadsApi.uploadAvatar(currentWorkspaceId, result.id, file);
								updateStatus("avatar", { status: "completed", progress: 100 });
							} catch (err) {
								console.error("Avatar upload failed:", err);
								updateStatus("avatar", { status: "error" });
								hasUploadFailure = true;
								failedUploads.push("Avatar");
							}
						})(),
					);
				}

				// Company logo upload (only new base64 images)
				if (isNewImage(companyLogoImage) && companyLogoImage) {
					uploadPromises.push(
						(async () => {
							updateStatus("logo", { status: "uploading", progress: 50 });
							try {
								const blob = dataUrlToBlob(companyLogoImage);
								const file = new File([blob], "logo.png", { type: blob.type });
								await reviewUploadsApi.uploadCompanyLogo(currentWorkspaceId, result.id, file);
								updateStatus("logo", { status: "completed", progress: 100 });
							} catch (err) {
								console.error("Company logo upload failed:", err);
								updateStatus("logo", { status: "error" });
								hasUploadFailure = true;
								failedUploads.push("Logo");
							}
						})(),
					);
				}

				// Wait for all uploads to complete
				await Promise.all(uploadPromises);

				// Check if any uploads failed
				if (hasUploadFailure) {
					setError(
						`${failedUploads.join(" and ")} upload${failedUploads.length > 1 ? "s" : ""} failed. The review has been saved, but some files may not be attached. You can edit the review to retry.`,
					);
					setIsUploading(false);
					// Keep modal closed and show error in the form
					// In create mode, still call onSuccess so user can see the review was created
					if (!editMode) {
						onSuccess(result);
					}
					return;
				}

				// Small delay to show completion state
				await new Promise((resolve) => setTimeout(resolve, 500));

				setIsUploading(false);
			}

			// In edit mode, reset form state and show saved indicator
			if (editMode) {
				// Update form with new values and reset dirty state
				reset({
					contentText: result.content_text || "",
					date: formatDateForInput(result.date),
					customerName: result.customer_name || "",
					consent: (result.consent as "public" | "private" | "anonymous") || "public",
					rating: result.rating || 0,
					customerHeadline: result.customer_headline || "",
					customerEmail: result.customer_email || "",
					customerPhone: result.customer_phone || "",
					customerJobTitle: result.customer_job_title || "",
					customerCompanyName: result.customer_company_name || "",
					customerCompanyWebsiteUrl: result.customer_company_website_url || "",
				});

				// Reset state to match saved values
				setSource(result.source || "manual");
				setSourcePostUrl(result.source_post_url || "");

				// Merge saved tags into store to ensure newly created tags are available
				// Use result.tags if available, otherwise keep current selection
				const savedTagIds = result.tags && result.tags.length > 0 ? result.tags.map((t) => t.id) : selectedTagIds;
				if (result.tags && result.tags.length > 0) {
					mergeSelectedTags(result.tags);
				}
				setSelectedTagIds(savedTagIds);

				// For images: if we just uploaded a new image (base64), keep showing it until the API returns the URL
				// This prevents the preview from going blank while the upload is processing
				// Compute the final values first so we can use them for both state and baseline
				const finalAvatarImage = result.customer_avatar_url || (avatarImage?.startsWith("data:") ? avatarImage : null);
				const finalCompanyLogoImage = result.customer_company_logo_url || (companyLogoImage?.startsWith("data:") ? companyLogoImage : null);

				setAvatarImage(finalAvatarImage);
				setCompanyLogoImage(finalCompanyLogoImage);
				setRatingMode(result.rating && result.rating > 0 ? "with-rating" : "without-rating");
				setManualType(!result.rating || result.rating === 0 ? (result.type as "testimonial" | "feedback") || null : null);

				// Update baseline data to the new saved state for future comparisons
				// Include the computed image values and tags so hasChanges works correctly
				setBaselineData({
					...result,
					tags: result.tags && result.tags.length > 0 ? result.tags : initialData?.tags?.filter((t) => savedTagIds.includes(t.id)) || [],
					customer_avatar_url: finalAvatarImage,
					customer_company_logo_url: finalCompanyLogoImage,
				});

				setSaveStatus("saved");
				setIsLoading(false);

				// Hide "Saved" after 2 seconds
				savedTimeoutRef.current = setTimeout(() => {
					setSaveStatus("idle");
				}, 2000);
			} else {
				// In create mode, refetch plan data and navigate to the new review
				if (currentWorkspaceId) {
					await getPlan(currentWorkspaceId);
				}
				onSuccess(result);
			}
		} catch (err) {
			const error = err as Error & { isPlanLimit?: boolean; planLimitInfo?: PlanLimitErrorInfo };
			if (error.isPlanLimit && error.planLimitInfo) {
				const feature = error.planLimitInfo.feature;
				const titles: Record<string, string> = {
					max_text_reviews: "Text Review Limit Reached",
					max_video_reviews: "Video Review Limit Reached",
					max_audio_reviews: "Audio Review Limit Reached",
				};
				const messages: Record<string, string> = {
					max_text_reviews: "You've reached your limit for text reviews on the free plan. Upgrade to Growth to collect unlimited text testimonials and build your review library.",
					max_video_reviews:
						"You've reached your limit for video reviews on the free plan. Upgrade to Growth to collect unlimited video testimonials and showcase your customers' experiences.",
					max_audio_reviews: "You've reached your limit for audio reviews on the free plan. Upgrade to Growth to collect unlimited audio testimonials and add variety to your social proof.",
				};
				openUpgradeModal({
					title: titles[feature] || "Plan Limit Reached",
					message: messages[feature] || error.message,
					userRole: selectedBrand?.role,
				});
				setIsLoading(false);
				setSaveStatus("idle");
			} else {
				setError(err instanceof Error ? err.message : "An error occurred while saving the review");
				setSaveStatus("error");
				setIsLoading(false);
				savedTimeoutRef.current = setTimeout(() => {
					setSaveStatus("idle");
				}, 3000);
			}
		}
	};

	const handleFormatChange = (format: ReviewFormat) => {
		if (format === "video" && planDetails && hasReachedReviewLimit(planDetails.usage, planDetails.limits, "video")) {
			openUpgradeModal({
				title: "Video Review Limit Reached",
				message: "You've reached your limit for video reviews on the free plan. Upgrade to Growth to collect unlimited video testimonials and showcase your customers' experiences.",
				userRole: selectedBrand?.role,
			});
			return;
		}
		if (format === "audio" && planDetails && hasReachedReviewLimit(planDetails.usage, planDetails.limits, "audio")) {
			openUpgradeModal({
				title: "Audio Review Limit Reached",
				message: "You've reached your limit for audio reviews on the free plan. Upgrade to Growth to collect unlimited audio testimonials and add variety to your social proof.",
				userRole: selectedBrand?.role,
			});
			return;
		}
		if (format === "text" && planDetails && hasReachedReviewLimit(planDetails.usage, planDetails.limits, "text")) {
			openUpgradeModal({
				title: "Text Review Limit Reached",
				message: "You've reached your limit for text reviews on the free plan. Upgrade to Growth to collect unlimited text testimonials and build your review library.",
				userRole: selectedBrand?.role,
			});
			return;
		}

		if (reviewFormat !== format) {
			setValue("contentText", "", { shouldDirty: true });
			setVideoFile(null);
			setAudioFile(null);
		}
		setReviewFormat(format);
	};

	return (
		<>
			{/* Upload Progress Modal */}
			{isUploading && uploadStatuses.length > 0 && <UploadProgressModal uploads={uploadStatuses} />}

			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="space-y-8">
					{/* Rating Section */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						{/* Header */}
						<div className="flex items-start gap-3 p-6">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Rating</h3>
								<p className="text-t-13 text-tertiary mt-0.5">Does this review have a star rating?</p>
							</div>
						</div>

						{/* With/Without Rating Grouped Button */}
						<div className="border-t border-gray-200 p-6">
							<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
								<button
									type="button"
									onClick={() => {
										setRatingMode("with-rating");
										// Reset rating to 0 and clear manual type when switching to with-rating mode
										setValue("rating", 0, { shouldDirty: true });
										setManualType(null);
									}}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
										ratingMode === "with-rating" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-secondary"
									}`}
								>
									{ratingMode === "with-rating" ? (
										<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									) : (
										<IconStarFill className="w-4 h-4" />
									)}
									With Rating
								</button>
								<button
									type="button"
									onClick={() => {
										setRatingMode("without-rating");
										// Clear rating when switching to without-rating mode
										setValue("rating", null, { shouldDirty: true });
										// Manual type will need to be selected by user
									}}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
										ratingMode === "without-rating" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-secondary"
									}`}
								>
									{ratingMode === "without-rating" ? (
										<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									) : (
										<IconStarLine className="w-4 h-4" />
									)}
									Without Rating
								</button>
							</div>
						</div>

						{/* Star Rating (only shown in with-rating mode) */}
						{ratingMode === "with-rating" && (
							<div className="px-6 pb-6 flex items-center">
								<div className="w-1/2">
									<StarRatingInput
										value={ratingValue ?? 0}
										onChange={(rating) => {
											setValue("rating", rating, { shouldDirty: true });
										}}
									/>
								</div>
								{/* Classification indicator - same tile style as selector, 50% width */}
								{ratingValue !== null && ratingValue > 0 && (
									<div className="w-1/2 pl-6">
										<div
											className={`px-4 py-3 rounded-lg border text-left flex items-center gap-3 ${
												ratingValue >= 4 ? "border-green-500 bg-green-50 text-green-700" : "border-blue-500 bg-blue-50 text-blue-700"
											}`}
										>
											{ratingValue >= 4 ? (
												<>
													<IconHeart className="w-5 h-5 flex-shrink-0" solid />
													<div>
														<div className="font-medium text-sm">Testimonial</div>
														<div className="text-xs opacity-75">Positive feedback</div>
													</div>
												</>
											) : (
												<>
													<IconChatBubbleSquare className="w-5 h-5 flex-shrink-0" solid />
													<div>
														<div className="font-medium text-sm">Feedback</div>
														<div className="text-xs opacity-75">Neutral or constructive</div>
													</div>
												</>
											)}
										</div>
									</div>
								)}
							</div>
						)}

						{/* Manual Classification (only shown in without-rating mode) */}
						{ratingMode === "without-rating" && (
							<div className="px-6 pb-6">
								<div className="flex gap-3">
									<button
										type="button"
										onClick={() => setManualType("testimonial")}
										className={`flex-1 px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
											manualType === "testimonial" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 hover:border-gray-300 text-secondary"
										}`}
									>
										<IconHeart className="w-5 h-5 flex-shrink-0" solid={manualType === "testimonial"} />
										<div>
											<div className="font-medium text-sm">Testimonial</div>
											<div className="text-xs opacity-75">Positive feedback</div>
										</div>
									</button>
									<button
										type="button"
										onClick={() => setManualType("feedback")}
										className={`flex-1 px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
											manualType === "feedback" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 hover:border-gray-300 text-secondary"
										}`}
									>
										<IconChatBubbleSquare className="w-5 h-5 flex-shrink-0" solid={manualType === "feedback"} />
										<div>
											<div className="font-medium text-sm">Feedback</div>
											<div className="text-xs opacity-75">Neutral or constructive</div>
										</div>
									</button>
								</div>
							</div>
						)}

						{/* Error */}
						{errors.rating && <p className="text-xs text-red-500 px-6 pb-4">{errors.rating.message}</p>}
					</div>

					{/* Review Information Section (with Format Tabs) */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						{/* Header */}
						<div className="flex items-start gap-3 p-6">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Review Information</h3>
								<p className="text-t-13 text-tertiary mt-0.5">What did they say about you?</p>
							</div>
						</div>

						{/* Format Selection Grouped Button */}
						<div className={`border-t border-gray-200 p-6 ${editMode ? "opacity-60" : ""}`}>
							<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
								<button
									type="button"
									onClick={() => {
										if (editMode) return;
										handleFormatChange("text");
									}}
									disabled={editMode}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
										reviewFormat === "text" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-secondary"
									} ${editMode ? "cursor-not-allowed" : ""}`}
								>
									{reviewFormat === "text" ? (
										<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									) : (
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
											/>
										</svg>
									)}
									Text
								</button>
								<button
									type="button"
									onClick={() => {
										if (editMode) return;
										handleFormatChange("video");
									}}
									disabled={editMode}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
										reviewFormat === "video" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-secondary"
									} ${editMode ? "cursor-not-allowed" : ""}`}
								>
									{reviewFormat === "video" ? (
										<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									) : (
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
											/>
										</svg>
									)}
									Video
								</button>
								<button
									type="button"
									onClick={() => {
										if (editMode) return;
										handleFormatChange("audio");
									}}
									disabled={editMode}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
										reviewFormat === "audio" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-secondary"
									} ${editMode ? "cursor-not-allowed" : ""}`}
								>
									{reviewFormat === "audio" ? (
										<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									) : (
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
											/>
										</svg>
									)}
									Audio
								</button>
							</div>
						</div>

						{/* Content */}
						<div className="border-t border-gray-200 p-6 space-y-5">
							{/* Text Format Fields */}
							{reviewFormat === "text" && (
								<Textarea label="Content" rows={4} placeholder="Share your review..." required disabled={isLoading} error={errors.contentText?.message} {...register("contentText")} />
							)}

							{/* Video Format Fields */}
							{reviewFormat === "video" && (
								<>
									{editMode && initialData?.mux_playback_id ? (
										<div>
											<label className="block text-sm font-medium text-primary mb-1.5">Video</label>
											<div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
												<div className="aspect-video relative">
													<img
														src={initialData.video_thumbnail_url || `https://image.mux.com/${initialData.mux_playback_id}/thumbnail.jpg?time=0`}
														alt="Video thumbnail"
														className="w-full h-full object-cover"
													/>
													<div className="absolute inset-0 flex items-center justify-center bg-black/30">
														<div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
															<svg className="w-8 h-8 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
																<path d="M8 5v14l11-7z" />
															</svg>
														</div>
													</div>
												</div>
											</div>
											<p className="text-xs text-tertiary mt-2">Video cannot be changed after creation.</p>
										</div>
									) : (
										!editMode && (
											<div>
												<label className="block text-sm font-medium text-primary mb-1.5">
													Video File <span className="text-red-500">*</span>
												</label>
												<VideoUploadField onFileSelect={setVideoFile} disabled={isLoading} />
												{!videoFile && <p className="text-xs text-tertiary mt-2">Video will be uploaded after you save the review</p>}
											</div>
										)
									)}

									<Textarea label="Excerpt" rows={3} placeholder="Optional text excerpt or summary of the video..." disabled={isLoading} {...register("contentText")} />
								</>
							)}

							{/* Audio Format Fields */}
							{reviewFormat === "audio" && (
								<>
									{editMode && initialData?.audio_url ? (
										<div>
											<label className="block text-sm font-medium text-primary mb-1.5">Audio</label>
											<AudioPlayer src={initialData.audio_url} initialDuration={initialData.audio_duration} />
											<p className="text-xs text-tertiary mt-2">Audio cannot be changed after creation.</p>
										</div>
									) : (
										!editMode && (
											<div>
												<label className="block text-sm font-medium text-primary mb-1.5">
													Audio File <span className="text-red-500">*</span>
												</label>
												<AudioUploadField onFileSelect={setAudioFile} disabled={isLoading} />
												{!audioFile && <p className="text-xs text-tertiary mt-2">Audio will be uploaded after you save the review</p>}
											</div>
										)
									)}

									<Textarea label="Excerpt" rows={3} placeholder="Optional text excerpt or summary of the audio..." disabled={isLoading} {...register("contentText")} />
								</>
							)}

							{/* Date - Common for all formats */}
							<Input label="Date" type="date" required disabled={isLoading} error={errors.date?.message} max={new Date().toISOString().split("T")[0]} {...register("date")} />

							{/* Source Fields */}
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<Select
									label="Source"
									value={source}
									onChange={setSource}
									placeholder="Select source..."
									disabled={isLoading}
									options={[
										{ value: "manual", label: "Manual Entry" },
										{ value: "twitter", label: "Twitter / X.com" },
										{ value: "linkedin", label: "LinkedIn" },
										{ value: "facebook", label: "Facebook" },
										{ value: "instagram", label: "Instagram" },
										{ value: "google_reviews", label: "Google Reviews" },
										{ value: "capterra", label: "Capterra" },
										{ value: "email", label: "Email" },
										{ value: "other", label: "Other" },
									]}
								/>
								<Input
									label="Source URL"
									type="url"
									placeholder="e.g. https://x.com/user/status/123..."
									disabled={isLoading}
									value={sourcePostUrl}
									onChange={(e) => setSourcePostUrl(e.target.value)}
								/>
							</div>
						</div>
					</div>

					{/* Consent Section */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						{/* Header */}
						<div className="flex items-start gap-3 p-6">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Consent & Usage</h3>
								<p className="text-t-13 text-tertiary mt-0.5">How can this review be used?</p>
							</div>
						</div>

						{/* Content */}
						<div className="border-t border-gray-200 p-6">
							<div className="flex flex-col gap-3">
								<button
									type="button"
									onClick={() => {
										setValue("consent", "public", { shouldDirty: true });
										trigger("consent");
									}}
									className={`px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
										consent === "public" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 hover:border-gray-300 text-secondary"
									}`}
								>
									<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1.5}
											d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									<div>
										<div className="font-medium text-sm">Share it publicly</div>
										<div className="text-xs opacity-75">On your website, social media, and marketing materials</div>
									</div>
								</button>
								<button
									type="button"
									onClick={() => {
										setValue("consent", "anonymous", { shouldDirty: true });
										trigger("consent");
									}}
									className={`px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
										consent === "anonymous" ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 hover:border-gray-300 text-secondary"
									}`}
								>
									<IconAnonymous className="w-5 h-5 shrink-0" />
									<div>
										<div className="font-medium text-sm">Share it anonymously</div>
										<div className="text-xs opacity-75">On your website, social media, and marketing materials</div>
									</div>
								</button>
								<button
									type="button"
									onClick={() => {
										setValue("consent", "private", { shouldDirty: true });
										trigger("consent");
									}}
									className={`px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
										consent === "private" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 hover:border-gray-300 text-secondary"
									}`}
								>
									<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1.5}
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
										/>
									</svg>
									<div>
										<div className="font-medium text-sm">Keep it private</div>
										<div className="text-xs opacity-75">For internal use and feedback only</div>
									</div>
								</button>
							</div>
						</div>
					</div>

					{/* Person Information Section */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						{/* Header */}
						<div className="flex items-start gap-3 p-6">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Person Information</h3>
								<p className="text-t-13 text-tertiary mt-0.5">Who gave you this review?</p>
							</div>
						</div>

						{/* Content */}
						<div className="border-t border-gray-200 p-6 space-y-5">
							<Input label="Name" type="text" placeholder="e.g. John Smith" required disabled={isLoading} error={errors.customerName?.message} {...register("customerName")} />
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<Input label="Email" type="email" placeholder="e.g. john@company.com" disabled={isLoading} error={errors.customerEmail?.message} {...register("customerEmail")} />
								<Input label="Phone" type="tel" placeholder="e.g. +1 (555) 123-4567" disabled={isLoading} error={errors.customerPhone?.message} {...register("customerPhone")} />
							</div>
							<ImageUploadField label="Photo" value={avatarImage} onChange={setAvatarImage} aspectRatio={1} maxDimension={256} disabled={isLoading} className="max-w-32" />
						</div>
					</div>

					{/* Company Information Section */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						{/* Header */}
						<div className="flex items-start gap-3 p-6">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Company Information</h3>
								<p className="text-t-13 text-tertiary mt-0.5">Where do they work?</p>
							</div>
						</div>

						{/* Content */}
						<div className="border-t border-gray-200 p-6 space-y-5">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<Input
									label="Job Title"
									type="text"
									placeholder="e.g. Head of Marketing"
									disabled={isLoading}
									error={errors.customerJobTitle?.message}
									{...register("customerJobTitle")}
								/>
								<Input label="Company" type="text" placeholder="e.g. Acme Inc." disabled={isLoading} error={errors.customerCompanyName?.message} {...register("customerCompanyName")} />
							</div>
							<Input
								label="Website"
								type="url"
								placeholder="e.g. https://acme.com"
								disabled={isLoading}
								error={errors.customerCompanyWebsiteUrl?.message}
								{...register("customerCompanyWebsiteUrl")}
							/>
							<ImageUploadField label="Logo" value={companyLogoImage} onChange={setCompanyLogoImage} maxDimension={256} disabled={isLoading} className="max-w-48" />
						</div>
					</div>

					{/* Tags Section */}
					<div className="bg-white border border-gray-200 rounded-xl">
						{/* Header */}
						<div className="flex items-start gap-3 p-6">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Tags</h3>
								<p className="text-t-13 text-tertiary mt-0.5">Organize this review with tags for easy filtering</p>
							</div>
						</div>

						{/* Content */}
						<div className="border-t border-gray-200 p-6">
							<TagSelector
								tags={tags || []}
								selectedTagIds={selectedTagIds}
								onChange={setSelectedTagIds}
								disabled={isLoading}
								placeholder="Search and select tags..."
								onCreateTag={handleCreateTag}
								hasMore={!!tagsNextCursor}
								isLoadingMore={isLoadingMoreTags}
								onLoadMore={handleLoadMoreTags}
								onSearch={handleSearchTags}
								onTagSelect={handleTagSelect}
							/>
						</div>
					</div>

					{error && (
						<div className="bg-red-50 border border-red-200 rounded-md p-3">
							<p className="text-sm text-red-600">{error}</p>
						</div>
					)}

					{/* Spacer for sticky footer */}
					<div className="h-24" />
				</div>

				{/* Sticky footer with Save/Cancel buttons */}
				<div
					className={`fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 transition-all duration-300 ${isSidebarCollapsed ? "w-[calc(100%-64px)] ml-16" : "w-[calc(100%-256px)] ml-64"}`}
				>
					<Container>
						<div className="flex items-center justify-end gap-3">
							{/* Status indicator (edit mode only) */}
							{editMode && saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							<Button type="button" onClick={handleDiscard} disabled={isLoading || isUploading || !hasChanges || saveStatus === "saving" || saveStatus === "saved"} variant="light">
								{editMode ? "Discard" : "Cancel"}
							</Button>

							<Button type="submit" disabled={isLoading || isUploading || !isFormComplete || !hasChanges || saveStatus === "saving" || saveStatus === "saved"} variant="default">
								{isLoading
									? editMode
										? "Updating..."
										: "Saving..."
									: isUploading
										? "Uploading..."
										: saveStatus === "saving"
											? "Saving..."
											: editMode
												? "Save Changes"
												: "Save Review"}
							</Button>
						</div>
					</Container>
				</div>
			</form>
		</>
	);
}
